/**
 * v2 credit-ledger reason humanization (DNKADM-0059 U2, handoff §5.14).
 *
 * The v2 ledger reasons are a fixed token set:
 *   `booking_debit | cancel_refund | monthly_refill | package_grant |
 *    standalone_purchase | admin_adjust | admin_lot_reduce | expiry |
 *    expiry_change | import`
 * These render as staff-facing labels (the ledger table + the per-lot source line).
 * A known reason maps to its clean label (local constants — no scrub needed); an
 * unknown/null token degrades to a humanized fallback (the raw token title-cased),
 * brand-scrubbed at the render boundary (§4.0) so a stray "GHL"/"GoHighLevel" in a
 * backend-sourced token surfaces as "Journix". Never a fabricated reason.
 */
import { scrubBrand } from '@/utils/whitelabel';

/** The fixed v2 ledger-reason token set (handoff §5.14). */
export const V2_LEDGER_REASONS = [
  'booking_debit',
  'cancel_refund',
  'monthly_refill',
  'package_grant',
  'standalone_purchase',
  'admin_adjust',
  'admin_lot_reduce',
  'expiry',
  'expiry_change',
  'import',
] as const;

/** One v2 ledger reason token. */
type V2LedgerReason = (typeof V2_LEDGER_REASONS)[number];

/** Clean, staff-facing labels for each known reason (brand-neutral local copy). */
const REASON_LABELS: Record<V2LedgerReason, string> = {
  booking_debit: 'Booking',
  cancel_refund: 'Cancellation refund',
  monthly_refill: 'Monthly refill',
  package_grant: 'Package grant',
  standalone_purchase: 'Purchase',
  admin_adjust: 'Admin adjustment',
  admin_lot_reduce: 'Credit reduction',
  expiry: 'Expired',
  expiry_change: 'Expiry change',
  import: 'Import',
};

/** Title-case a snake/kebab/space token for an unknown reason (no fabrication). */
function humanize(raw: string): string {
  const words = raw
    .trim()
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.length === 0) return 'Credit change';
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Humanize a v2 ledger reason token to a staff-facing label. A known token maps to its
 * clean label; an unknown/null token degrades to the title-cased raw token (brand-
 * scrubbed at this render boundary), never a fabricated reason.
 */
export function ledgerReasonLabel(reason: string | null | undefined): string {
  if (reason == null) return 'Credit change';
  const key = reason.trim().toLowerCase();
  if ((V2_LEDGER_REASONS as readonly string[]).includes(key)) {
    return REASON_LABELS[key as V2LedgerReason];
  }
  return scrubBrand(humanize(reason)) ?? 'Credit change';
}
